import { useState, useEffect, Suspense, lazy as _lazy } from "react";
import { Switch, Route, useLocation } from "wouter";
import { Toaster } from "react-hot-toast";

import ErrorBoundary from "@/ErrorBoundary";
import { useAuth } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Footer from "@/components/Footer";

import { MascotProvider } from "@/contexts/mascot-context";
import HaasOrientation from "@/components/onboarding/haas-orientation";
import { SafeTimerProvider } from "@/contexts/SafeTimerProvider";
import { SafeCareRecipientViewProvider } from "@/contexts/SafeCareRecipientViewProvider";
import { ThemeProvider } from "@/lib/theme-context";
import { HubProvider, useHub } from "@/contexts/HubProvider";
import { HubSetter } from "@/components/HubSetter";

import { useAuthClient } from "@/contexts/AuthClientContext";
import { useNearbyBoards } from "@/hooks/useNearbyBoards";
import { useQuery } from "@tanstack/react-query";

// STATIC import for HubTimers to avoid 429 rate limiting on dynamic imports
import HubTimers from "./pages/hub-timers-fixed";

/**
 * Wraps React.lazy so that a stale-chunk 404 (common after a deploy while a
 * user still has the old HTML cached) triggers a one-time page reload instead
 * of crashing into the ErrorBoundary.  SessionStorage prevents infinite loops.
 */
function lazyWithRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return _lazy(() =>
    factory().catch((err: unknown) => {
      const isChunkError =
        err instanceof TypeError &&
        (err.message.includes("Failed to fetch dynamically imported module") ||
          err.message.includes("Importing a module script failed"));

      if (isChunkError) {
        const key = "chunk_reload_attempted";
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          window.location.reload();
          // Never resolves — the reload takes over
          return new Promise<{ default: T }>(() => {});
        }
      }
      throw err;
    })
  );
}

/* ---------- Lazy Page Imports ---------- */
const Landing = lazyWithRetry(() => import("./pages/Landing"));
const MinimalSignUp = lazyWithRetry(() => import("./pages/MinimalSignUp"));
const HubSelector = lazyWithRetry(() => import("./pages/hub-selector"));
const Auth0Callback = lazyWithRetry(() => import("./pages/auth0-callback"));
const HubDashboard = lazyWithRetry(() => import("./pages/hub-dashboard"));
const HubSettingsNew = lazyWithRetry(() => import("./pages/hub-settings-new"));
const CommunityFeed = lazyWithRetry(() => import("./pages/community-feed"));
const YourVillage = lazyWithRetry(() => import("./pages/your-village"));
const Profile = lazyWithRetry(() => import("./pages/profile"));
const Settings = lazyWithRetry(() => import("./pages/settings"));
const SubscriptionManagement = lazyWithRetry(() => import("./pages/subscription-management"));
const Help = lazyWithRetry(() => import("./pages/help"));

// Hub Feature Pages
const PictureSchedules = lazyWithRetry(() => import("./pages/picture-schedules"));
const ChoiceBoards = lazyWithRetry(() => import("./pages/choice-boards"));
const ChoiceBoardDetail = lazyWithRetry(() => import("./pages/choice-board-detail"));
const RemindersHub = lazyWithRetry(() => import("./pages/reminders-hub"));
const RoutinesHub = lazyWithRetry(() => import("./pages/routines-hub"));
const VideoModelsHub = lazyWithRetry(() => import("./pages/video-models-hub"));
const SocialNarratives = lazyWithRetry(() => import("./pages/social-narratives"));
const LocationPrep = lazyWithRetry(() => import("./pages/location-prep"));
const RewardChartsGrid = lazyWithRetry(() => import("./pages/reward-charts-grid"));
const HealthForm = lazyWithRetry(() => import("./pages/health-form"));
const ProgressTracking = lazyWithRetry(() => import("./pages/progress-tracking"));
const AIPlans = lazyWithRetry(() => import("./pages/ai-plans"));
const MessengerHub = lazyWithRetry(() => import("./pages/messenger-hub-complete"));
const SharingSettings = lazyWithRetry(() => import("./pages/sharing-settings"));
const ReminderCreate = lazyWithRetry(() => import("./pages/reminder-create"));
const ReminderEdit = lazyWithRetry(() => import("./pages/reminder-edit"));
const ReminderShare = lazyWithRetry(() => import("./pages/reminder-share"));

const SimplifiedHubSettings = lazyWithRetry(() => import("./pages/simplified-hub-settings"));
const AdminPage = lazyWithRetry(() => import("./pages/admin"));
const BetaDashboard = lazyWithRetry(() => import("./pages/beta-dashboard"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/legal/privacy"));
const TermsOfService = lazyWithRetry(() => import("./pages/legal/terms"));
const HealthDisclaimer = lazyWithRetry(() => import("./pages/legal/health-disclaimer"));
const AcceptableUse = lazyWithRetry(() => import("./pages/legal/acceptable-use"));
const CommunityGuidelines = lazyWithRetry(() => import("./pages/legal/community-guidelines"));
const DMCAPolicy = lazyWithRetry(() => import("./pages/legal/dmca"));
const DataDeletion = lazyWithRetry(() => import("./pages/legal/data-deletion"));
const CookiePolicy = lazyWithRetry(() => import("./pages/legal/cookies"));
const SecurityPolicy = lazyWithRetry(() => import("./pages/legal/security"));
const AccessibilityStatement = lazyWithRetry(() => import("./pages/legal/accessibility"));
const BetaTerms = lazyWithRetry(() => import("./pages/legal/beta"));
const LegalIndex = lazyWithRetry(() => import("./pages/legal/index"));
const SupportPage = lazyWithRetry(() => import("./pages/support"));
const InviteAccept = lazyWithRetry(() => import("./pages/invite-accept"));
const Welcome = lazyWithRetry(() => import("./pages/welcome"));
const InviteDashboard = lazyWithRetry(() => import("./pages/invite-dashboard"));
const SupportInbox = lazyWithRetry(() => import("./pages/support-inbox"));

// Reward Chart Detail Pages - Temporarily disabled
// TODO: Test these pages individually
// const RewardChartCreate = lazy(() => import("./pages/reward-charts/create"));
// const RewardChartDetail = lazy(() => import("./pages/reward-charts/[id]/index"));
// const RewardChartEdit = lazy(() => import("./pages/reward-charts/[id]/edit"));
// const RewardChartShare = lazy(() => import("./pages/reward-charts/[id]/share"));

// Care Recipient View - PUBLIC route (no auth required) - STATIC to avoid 429
import CareRecipientView from "./pages/care-recipient-view";
import CarePinGate from "@/components/care-recipient/care-pin-gate";

function ProtectedCareView(props: any) {
  const hubId = new URLSearchParams(window.location.search).get("hubId") || "";
  return (
    <CarePinGate hubId={hubId}>
      <CareRecipientView {...props} />
    </CarePinGate>
  );
}

// Other Care Recipient Pages - Temporarily disabled
// TODO: Fix pre-existing bugs in these files
// const CareRecipientTimers = lazy(() => import("./pages/care-recipient-visual-timers"));
// const CareRecipientSchedules = lazy(() => import("./pages/care-recipient-schedules"));
// const CareRecipientRewardCharts = lazy(() => import("./pages/care-recipient-reward-charts"));
// const CareRecipientVideos = lazy(() => import("./pages/care-recipient-videos"));
// const CareRecipientLocationGuides = lazy(() => import("./pages/care-recipient-location-guides"));
// const CareRecipientReminders = lazy(() => import("./pages/care-recipient-reminders"));
// const CareRecipientMessenger = lazy(() => import("./pages/care-recipient-messenger"));
// const CareRecipientExit = lazy(() => import("./pages/care-recipient-exit"));

/* ---------- Hubs Entry ---------- */
function HubsEntry() {
  const { isAuthenticated } = useAuth();
  const { loading: hubLoading } = useHub();

  // ProtectedRoute already ensures user is authenticated, but double-check for clarity
  if (!isAuthenticated) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-muted-foreground">
        Authenticating…
      </div>
    );
  }

  // Show loading while hubs are being fetched
  if (hubLoading) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-muted-foreground">
        Loading hubs…
      </div>
    );
  }

  // Always show HubSelector for authenticated users (Lite, Premium, Plus, etc.)
  // HubSelector handles:
  // - "No hubs" case with "Create your first hub" button
  // - Hub creation limits with toast message
  // - ONLY redirects to /subscription when user hits hub creation limit (not on login)
  return <HubSelector />;
}

/* ---------- Router ---------- */
function AppRouter() {
  return (
    <Suspense fallback={<div>Loading…</div>}>
      <div data-testid="app-shell" style={{ display: 'contents' }}>
      <Switch>
        <Route path="/" component={Landing} />
        <Route path="/signup" component={MinimalSignUp} />
        <Route path="/auth0/callback" component={Auth0Callback} />
        
        {/* Invite acceptance - PUBLIC page (auth handled inside) */}
        <Route path="/invite/:token" component={InviteAccept} />
        
        {/* Welcome screen after invite acceptance */}
        <Route path="/welcome" component={Welcome} />
        
        {/* Legal Pages - PUBLIC, no auth required */}
        <Route path="/legal/privacy" component={PrivacyPolicy} />
        <Route path="/legal/terms" component={TermsOfService} />
        <Route path="/legal/health-disclaimer" component={HealthDisclaimer} />
        <Route path="/legal/acceptable-use" component={AcceptableUse} />
        <Route path="/legal/community-guidelines" component={CommunityGuidelines} />
        <Route path="/legal/dmca" component={DMCAPolicy} />
        <Route path="/legal/data-deletion" component={DataDeletion} />
        <Route path="/legal/cookies" component={CookiePolicy} />
        <Route path="/legal/security" component={SecurityPolicy} />
        <Route path="/legal/accessibility" component={AccessibilityStatement} />
        <Route path="/legal/beta" component={BetaTerms} />
        <Route path="/legal" component={LegalIndex} />
        
        {/* Support - PUBLIC */}
        <Route path="/support" component={SupportPage} />

        {/* Care Recipient View - PUBLIC, PIN-protected if configured */}
        <Route path="/care-view" component={ProtectedCareView} />
        
        {/* Beta Dashboard */}
        <Route path="/beta">
          <ProtectedRoute>
            <BetaDashboard />
          </ProtectedRoute>
        </Route>

        {/* Admin */}
        <Route path="/admin">
          <ProtectedRoute allowedRoles={["Admin"]} requiredPermission="access:admin-page">
            <AdminPage />
          </ProtectedRoute>
        </Route>
        
        {/* Hub Routes — more specific paths first */}
        <Route path="/hubs/hub-settings-new">
          <ProtectedRoute>
            <HubSettingsNew />
          </ProtectedRoute>
        </Route>
        
        <Route path="/hubs">
          <ProtectedRoute>
            <HubsEntry />
          </ProtectedRoute>
        </Route>
        
        <Route path="/hub/:hubId">
          <ProtectedRoute>
            <HubDashboard />
          </ProtectedRoute>
        </Route>
        
        {/* Hub Feature Routes */}
        <Route path="/hub/:hubId/timers">
          <ProtectedRoute>
            <HubTimers />
          </ProtectedRoute>
        </Route>
        
        <Route path="/hub/:hubId/schedules">
          <ProtectedRoute>
            <PictureSchedules />
          </ProtectedRoute>
        </Route>
        
        <Route path="/hub/:hubId/choice-boards">
          <ProtectedRoute>
            <ChoiceBoards />
          </ProtectedRoute>
        </Route>
        
        <Route path="/choice-boards/:id">
          <ProtectedRoute>
            <ChoiceBoardDetail />
          </ProtectedRoute>
        </Route>
        
        <Route path="/hub/:hubId/reminders">
          <ProtectedRoute>
            <RemindersHub />
          </ProtectedRoute>
        </Route>
        
        <Route path="/hub/:hubId/reminders/create">
          <ProtectedRoute>
            <ReminderCreate />
          </ProtectedRoute>
        </Route>
        
        <Route path="/hub/:hubId/reminders/:id/edit">
          <ProtectedRoute>
            <ReminderEdit />
          </ProtectedRoute>
        </Route>
        
        <Route path="/hub/:hubId/reminders/:id/share">
          <ProtectedRoute>
            <ReminderShare />
          </ProtectedRoute>
        </Route>
        
        <Route path="/hub/:hubId/routines">
          <ProtectedRoute>
            <RoutinesHub />
          </ProtectedRoute>
        </Route>

        <Route path="/hub/:hubId/videos">
          <ProtectedRoute>
            <VideoModelsHub />
          </ProtectedRoute>
        </Route>
        
        <Route path="/hub/:hubId/social-narratives">
          <ProtectedRoute>
            <SocialNarratives />
          </ProtectedRoute>
        </Route>
        
        <Route path="/hub/:hubId/locations">
          <ProtectedRoute>
            <LocationPrep />
          </ProtectedRoute>
        </Route>
        
        <Route path="/hub/:hubId/rewards">
          <ProtectedRoute>
            <RewardChartsGrid />
          </ProtectedRoute>
        </Route>
        
        {/* Reward chart detail routes disabled temporarily */}
        {/* TODO: Test and re-enable these routes */}
        
        <Route path="/hub/:hubId/health">
          <ProtectedRoute>
            <HealthForm />
          </ProtectedRoute>
        </Route>
        
        <Route path="/hub/:hubId/progress">
          <ProtectedRoute>
            <ProgressTracking />
          </ProtectedRoute>
        </Route>
        
        <Route path="/hub/:hubId/ai-plans">
          <ProtectedRoute>
            <AIPlans />
          </ProtectedRoute>
        </Route>
        
        <Route path="/hub/:hubId/messenger">
          <ProtectedRoute>
            <MessengerHub />
          </ProtectedRoute>
        </Route>
        
        <Route path="/hub/:hubId/sharing">
          <ProtectedRoute>
            <SharingSettings />
          </ProtectedRoute>
        </Route>
        
        <Route path="/hub/:hubId/settings/simple">
          <ProtectedRoute>
            <SimplifiedHubSettings />
          </ProtectedRoute>
        </Route>
        
        {/* Care Recipient Routes - DISABLED temporarily due to pre-existing build errors */}
        {/* TODO: Fix syntax errors and missing imports, then re-enable */}
        
        {/* Community & Social */}
        <Route path="/community">
          <ProtectedRoute>
            <CommunityFeed />
          </ProtectedRoute>
        </Route>
        
        <Route path="/your-village">
          <ProtectedRoute>
            <YourVillage />
          </ProtectedRoute>
        </Route>
        
        {/* User Routes */}
        <Route path="/profile">
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        </Route>
        
        <Route path="/settings">
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        </Route>
        
        <Route path="/subscription">
          <ProtectedRoute>
            <SubscriptionManagement />
          </ProtectedRoute>
        </Route>
        
        <Route path="/help">
          <ProtectedRoute>
            <Help />
          </ProtectedRoute>
        </Route>
        
        <Route path="/invites">
          <ProtectedRoute>
            <InviteDashboard />
          </ProtectedRoute>
        </Route>
        
        <Route path="/support-inbox">
          <ProtectedRoute>
            <SupportInbox />
          </ProtectedRoute>
        </Route>
      </Switch>
      </div>
    </Suspense>
  );
}

/* ---------- App Shell ---------- */
function App() {
  const { isAuthenticated, isLoading } = useAuth();
  const { ready: authReady } = useAuthClient();
  const [error, setError] = useState<Error | null>(null);
  const [location, navigate] = useLocation();

  // Nearby choice board detection — runs on mount and app foregrounding
  useNearbyBoards();
  
  // PUBLIC routes that don't need auth - skip auth loading for these.
  // The root route is handled separately below so a restored session does not
  // briefly show the public Login UI during application-session bootstrap.
  const isPublicRoute =
    location === '/' ||
    location === '/signup' ||
    location === '/care-view' ||
    location.startsWith('/care-view?') ||
    location.startsWith('/invite/') ||
    location === '/welcome' ||
    location.startsWith('/legal/') ||
    location === '/legal' ||
    location === '/support';
  const isCareViewRoute =
    location === '/care-view' ||
    location.startsWith('/care-view?');

  const isRootRoute = location === '/';

  useEffect(() => {
    if (isRootRoute && authReady && !isLoading && isAuthenticated) {
      navigate('/hubs');
    }
  }, [authReady, isAuthenticated, isLoading, isRootRoute, navigate]);

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50 text-red-900">
        <div className="text-center space-y-4">
          <h2 className="text-xl font-semibold">Something went wrong</h2>
          <p>{error.message}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-600 text-white rounded"
          >
            Reload
          </button>
        </div>
      </div>
    );

  // The root route is the PWA entry point. Wait for the existing application
  // session bootstrap before deciding between Landing and the authenticated
  // hub entry point. Other public routes continue to render immediately.
  if (isRootRoute && (isLoading || !authReady)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Checking authentication…</p>
      </div>
    );
  }

  // Avoid rendering Landing for an authenticated root session while the
  // navigation effect above moves the user to /hubs.
  if (isRootRoute && isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Opening your hubs…</p>
      </div>
    );
  }

  // Skip auth loading for the remaining public routes - let them render immediately
  if (!isPublicRoute && (isLoading || !authReady))
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Checking authentication…</p>
      </div>
    );

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <MascotProvider>
          <SafeCareRecipientViewProvider isAuthReady={authReady}>
            <SafeTimerProvider isAuthReady={authReady}>
              {!isCareViewRoute && <AnnouncementBanner />}
              <AppRouter />
              {!isCareViewRoute && !isAuthenticated && <Footer />}
              {!isCareViewRoute && isAuthenticated && <HaasOrientation />}
              <Toaster />
            </SafeTimerProvider>
          </SafeCareRecipientViewProvider>
        </MascotProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

function AnnouncementBanner() {
  const { data: announcement } = useQuery<{ id: string; message: string; active: boolean } | null>({
    queryKey: ['/api/announcement/active'],
    refetchInterval: 60000,
  });

  if (!announcement || !announcement.active) return null;

  return (
    <div className="bg-amber-100 border-b border-amber-300 px-4 py-2 text-center text-sm text-amber-900 font-medium">
      {announcement.message}
    </div>
  );
}

export default App;
